import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { CreditCard } from '../data/shared/CreditCard';
import { CardData } from 'data/types/CardData';
import { EarnRateInfo } from 'data/types/CardRewardsEarnRates';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { CategoryEarnRatePill } from './CreditPills';
import { CardContainer } from './CardContainer';
import { formatAnnualFeeForMobile } from '../utils/formatAnnualFee';
import { CreditCardImage } from './design-system/CreditCardImage';

interface MobileCardContainerProps {
  card: CreditCard;
  cardData: CardData;
  isUsed: boolean;
  bestEarnRatesForCard: EarnRateInfo[];
  onCardClick: () => void;
  onRemoveCard: () => void;
}

export const MobileCardContainer: React.FC<MobileCardContainerProps> = ({
  card,
  cardData,
  isUsed,
  bestEarnRatesForCard,
  onCardClick,
  onRemoveCard,
}) => {
  // Guard against undefined cardData
  if (!cardData) {
    return null;
  }

  return (
    <CardContainer
      isClickable={isUsed}
      onClick={onCardClick}
      opacity={isUsed ? 1 : 0.5}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Card Image */}
        <div
          style={{
            flexShrink: 0,
            width: '60px',
            height: '40px',
            minWidth: '60px',
            minHeight: '40px',
          }}
        >
          <CreditCardImage
            imageName={cardData.imageName}
            alt={cardData.name}
            width={60}
            height={40}
            style={{
              objectFit: 'cover',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          />
        </div>

        {/* Card Content */}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '16px',
              fontWeight: '400',
              marginBottom: '4px',
              color: '#333',
              fontFamily: 'Merriweather',
            }}
          >
            {cardData.name}
          </div>
          <p
            style={{
              fontSize: '14px',
              marginBottom: '4px',
              marginTop: 0,
            }}
          >
            {`Earns ${formatRewardsType(cardData.rewardsType)}`}
          </p>
          <p
            style={{
              fontSize: '12px',
              marginBottom: '8px',
              marginTop: 0,
              color: '#666',
            }}
          >
            {formatAnnualFeeForMobile(cardData)}
          </p>

          {isUsed ? (
            // Used card - show categories
            <div style={{ marginBottom: '8px' }}>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                Best for:
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                {bestEarnRatesForCard.map((earnRateInfo, index) => (
                  <CategoryEarnRatePill
                    key={`${card}-${index}`}
                    category={earnRateInfo.category}
                    earnRate={earnRateInfo.earnRate}
                    rewardsType={earnRateInfo.rewardsType}
                  />
                ))}
              </div>
            </div>
          ) : (
            // Unused card - show message
            <div
              style={{
                fontSize: '14px',
                color: '#999',
                fontStyle: 'italic',
              }}
            >
              No best categories
            </div>
          )}
        </div>

        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemoveCard();
          }}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            color: '#666',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    </CardContainer>
  );
};
